import { paths, safeAppPath, isUlid } from './paths';
import {
  isAiCourseNotification, aiNotificationIcon as courseIcon, aiNotificationText as courseText,
  aiNotificationPath as coursePath, NOTIFICATIONS_REFRESH, refreshNotifications, NOTIFY_LAG_MS,
} from '../courseBuilder/notifications';

/*
 * Ebook Builder notifications in the bell (CONTRACTS: `ai_ebook_*`, the same
 * suffixes as `ai_course_*`). They share the social notifications table, so
 * the bell sees all three kinds; the `anyAi*` helpers below dispatch between
 * course and ebook ones so the bell has one code path for both.
 */
export const AI_EBOOK_NOTIFICATIONS = {
  ai_ebook_ready: { icon: '📘', key: 'ready', text: 'Your ebook “{{title}}” is ready to review' },
  ai_ebook_chapter_updated: { icon: '✨', key: 'chapterUpdated', text: 'Your AI agent updated a chapter in “{{title}}”' },
  ai_ebook_needs_input: { icon: '✋', key: 'needsInput', text: '“{{title}}” needs your input to continue' },
  ai_ebook_build_failed: { icon: '⚠️', key: 'buildFailed', text: 'The build of “{{title}}” stopped' },
  ai_ebook_shared: { icon: '🤝', key: 'shared', text: '{{actor}} shared the ebook “{{title}}” with you' },
  ai_ebook_review_approved: { icon: '✅', key: 'reviewApproved', text: '“{{title}}” was approved' },
  ai_ebook_review_changes: { icon: '📝', key: 'reviewChanges', text: 'Changes requested on “{{title}}”' },
  ai_ebook_review_rejected: { icon: '⛔', key: 'reviewRejected', text: '“{{title}}” was not approved' },
  ai_ebook_published: { icon: '🚀', key: 'published', text: '“{{title}}” is published' },
  ai_ebook_reported: { icon: '🚩', key: 'reported', text: '“{{title}}” was reported' },
  ai_ebook_rating: { icon: '⭐', key: 'rating', text: '{{actor}} rated “{{title}}”' },
};

export function isAiEbookNotification(n) {
  return !!n && typeof n.type === 'string' && Object.prototype.hasOwnProperty.call(AI_EBOOK_NOTIFICATIONS, n.type);
}

export function ebookNotificationIcon(n) {
  return AI_EBOOK_NOTIFICATIONS[n?.type]?.icon || '📘';
}

/* `t` is react-i18next's; the defaults keep the bell readable before any translation exists. */
export function ebookNotificationText(n, t) {
  const def = AI_EBOOK_NOTIFICATIONS[n?.type];
  const vars = {
    title: String(n?.ebook_title || n?.title || 'your ebook').slice(0, 120),
    actor: String(n?.actor_name || 'Someone').slice(0, 80),
  };
  if (!def) return vars.title;
  if (typeof t === 'function') return t(`ebookBuilder.notif.${def.key}`, def.text, vars);
  return def.text.replace('{{title}}', vars.title).replace('{{actor}}', vars.actor);
}

/* Where clicking it goes: the server's in-app path, else the ebook's page. */
export function ebookNotificationPath(n) {
  const p = safeAppPath(n?.path);
  if (p) return p;
  if (isUlid(n?.ebook_id)) {
    if (n.type === 'ai_ebook_needs_input' || n.type === 'ai_ebook_build_failed') return paths.build(n.ebook_id);
    if (['ai_ebook_ready', 'ai_ebook_chapter_updated', 'ai_ebook_review_changes'].includes(n.type)) return paths.edit(n.ebook_id);
    return paths.ebook(n.ebook_id);
  }
  return paths.mine();
}

// ── the bell: course and ebook notifications through one set of helpers ──────
export const isAiNotification = (n) => isAiCourseNotification(n) || isAiEbookNotification(n);
export const anyAiNotificationIcon = (n) => (isAiEbookNotification(n) ? ebookNotificationIcon(n) : courseIcon(n));
export const anyAiNotificationText = (n, t) => (isAiEbookNotification(n) ? ebookNotificationText(n, t) : courseText(n, t));
export const anyAiNotificationPath = (n) => (isAiEbookNotification(n) ? ebookNotificationPath(n) : coursePath(n));

/*
 * What the bell shows a member: course and ebook notifications always; the
 * social ones (like/comment/reply) only on a paid plan, as before.
 */
export function visibleNotifications(list, isPaid) {
  return (Array.isArray(list) ? list : []).filter((n) => isAiNotification(n) || isPaid);
}

export { NOTIFICATIONS_REFRESH, refreshNotifications, NOTIFY_LAG_MS };
